import * as React from 'react';
import clsx from 'clsx';
import { EchoAvatar } from '@/components/common/EchoAvatar';
import { formatMessageTime } from '@/utils/formatters';
import type { ContactListItemProps } from '../types';
import { getEchoDomain } from '@/utils/echoUtils';

/** Pill badge showing the number of unread messages. */
const UnreadBadge: React.FC<{ count: number }> = ({ count }) => (
  <div
    className={clsx(
      'absolute bottom-7 right-[24px]',
      'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5',
      'bg-accent-glow'
    )}
  >
    <span className='font-sans text-[8px] font-bold leading-none tracking-normal text-green-700'>
      {count > 99 ? '99+' : count}
    </span>
  </div>
);

/** Sidebar contact card with avatar, name, domain, and last message preview. */
export const ContactListItem: React.FC<ContactListItemProps> = React.memo(
  ({
    echo,
    isActive,
    onClick,
    unreadCount = 0,
    lastMessageContent,
    lastMessageAt,
    showCheckmark,
  }) => {
    const ref = React.useRef<HTMLButtonElement>(null);

    const messageContent = lastMessageContent || 'Start Chatting';
    const timeDisplay = lastMessageAt ? formatMessageTime(lastMessageAt) : '';
    const avatarSrc = echo.imageUrl;

    const highlighted = isActive && (showCheckmark ?? false);
    const activeOnXl = isActive && !(showCheckmark ?? false);

    const avatar = (
      <div className='mr-4 shrink-0'>
        <EchoAvatar
          src={avatarSrc}
          videoSrc={null}
          alt={echo.name}
          initials={echo.name.slice(0, 2)}
        />
      </div>
    );

    const nameColor = highlighted
      ? 'text-white'
      : activeOnXl
        ? 'text-brand-800 xl:text-white'
        : 'text-brand-800';
    const subtitleColor = highlighted
      ? 'text-text-selected-subtitle'
      : activeOnXl
        ? 'text-purple-400 xl:text-text-selected-subtitle'
        : 'text-purple-400';
    const messageColor = highlighted
      ? 'text-text-selected-message'
      : activeOnXl
        ? 'text-gray-600 xl:text-text-selected-message'
        : 'text-gray-600';
    const timeColor = highlighted
      ? 'text-white'
      : activeOnXl
        ? 'text-dark-300 xl:text-white'
        : 'text-dark-300';

    const infoContent = (
      <div className='flex min-w-0 flex-1 flex-col justify-center'>
        <div
          className={clsx(
            'mb-1 truncate pr-12 font-proxima font-bold uppercase leading-20 tracking-normal',
            'text-18 xl:text-20',
            nameColor
          )}
        >
          {echo.name}
        </div>

        <div
          className={clsx(
            'mb-2 truncate font-sans font-medium leading-21 tracking-normal',
            'text-14 xl:text-16',
            subtitleColor
          )}
        >
          {getEchoDomain(echo)}
        </div>

        <div
          className={clsx(
            'truncate pr-8 font-sans text-13 font-normal leading-21 tracking-normal',
            messageColor
          )}
        >
          {messageContent}
        </div>
      </div>
    );

    const timeSection = timeDisplay && (
      <div
        className={clsx(
          'absolute right-[24px] top-[24px]',
          'font-sans text-10 font-normal leading-21 tracking-normal',
          timeColor
        )}
      >
        {timeDisplay}
      </div>
    );

    return (
      <button
        ref={ref}
        type='button'
        onClick={onClick}
        className={clsx(
          'relative flex flex-row items-start pt-6 text-left',
          'h-[107px]',
          'w-full xl:w-[389px]',
          'rounded-24',
          'pl-0 pr-[20px] xl:px-[20px]',
          'transition-all duration-200',
          highlighted
            ? 'bg-brand-840 shadow-lg'
            : activeOnXl
              ? 'bg-transparent xl:bg-brand-840 xl:shadow-lg'
              : 'bg-transparent hover:bg-gray-50'
        )}
      >
        {avatar}
        {infoContent}
        {timeSection}
        {unreadCount > 0 && <UnreadBadge count={unreadCount} />}

        {!highlighted && (
          <div
            className='absolute bottom-0 left-[90px] right-0 block h-px bg-gray-450 xl:hidden'
            aria-hidden='true'
          />
        )}
      </button>
    );
  }
);

ContactListItem.displayName = 'ContactListItem';
